import "./InfoTooltip.scss";

import React, { useEffect, useMemo, useState } from "react";

import { MdInfoOutline } from "react-icons/md";

export enum InfoTooltipDirection {
  TOP_LEFT = "top-left",
  TOP_RIGHT = "top-right",
  BOTTOM_RIGHT = "bottom-right",
  BOTTOM_LEFT = "bottom-left",
}

const ICON_COLORS = {
  dark: "#6B2DEF",
  light: "#fff",
};

interface InfoTooltipProps {
  children: React.ReactNode;
  direction?: InfoTooltipDirection;
  size?: "reg" | "sm";
  rightLabel?: string | React.ReactNode;
  storybook?: boolean;
  textColor?: string;
  iconColor?: string;
}

export const InfoTooltip: React.FC<InfoTooltipProps> = React.memo(
  ({
    children,
    direction = InfoTooltipDirection.BOTTOM_RIGHT,
    size = "reg",
    rightLabel,
    storybook = false,
    textColor,
    iconColor = "dark",
  }): JSX.Element => {
    const [showTooltip, setShowTooltip] = useState<boolean>(false);
    const [resolvedDirection, setResolvedDirection] = useState<InfoTooltipDirection>(direction);
    const dimensions = useMemo(() => {
      return {
        width: size === "reg" ? "24px" : "18px",
        height: size === "reg" ? "24px" : "18px",
        color: textColor,
      };
    }, [textColor, size]);
    const tooltipTextStyle = useMemo(() => {
      if (resolvedDirection === InfoTooltipDirection.TOP_LEFT) {
        return {
          bottom: size === "reg" ? "32px" : "22px",
          right: size === "reg" ? "-6px" : "-15px",
        };
      } else if (resolvedDirection === InfoTooltipDirection.TOP_RIGHT) {
        return {
          bottom: size === "reg" ? "32px" : "22px",
          left: size === "reg" ? "-12px" : "-15px",
        };
      } else if (resolvedDirection === InfoTooltipDirection.BOTTOM_LEFT) {
        return {
          top: size === "reg" ? "32px" : "22px",
          right: size === "reg" ? "-6px" : "-15px",
        };
      } else {
        return {
          top: size === "reg" ? "32px" : "22px",
          left: size === "reg" ? "-12px" : "-15px",
        };
      }
    }, [resolvedDirection, size]);

    const verticallyBound = (parentDiv, childDiv) => {
      let parentRect = parentDiv.getBoundingClientRect();
      let childRect = childDiv.getBoundingClientRect();

      return parentRect.top <= childRect.top && parentRect.bottom >= childRect.bottom;
    };

    const horizontallyBound = (parentDiv, childDiv) => {
      let parentRect = parentDiv.getBoundingClientRect();
      let childRect = childDiv.getBoundingClientRect();

      return parentRect.left <= childRect.left && parentRect.right >= childRect.right;
    };

    const [isVerticallyBound, setIsVerticallyBound] = useState<boolean>(true);
    const [isHorizontallyBound, setIsHorizontallyBound] = useState<boolean>(true);
    useEffect(() => {
      if (showTooltip && !storybook) {
        const parentDiv = document.getElementById("root");
        const childDiv = document.getElementById("dropdownTooltipText");

        setIsVerticallyBound(verticallyBound(parentDiv, childDiv));
        setIsHorizontallyBound(horizontallyBound(parentDiv, childDiv));
      } else {
        setIsVerticallyBound(true);
        setIsHorizontallyBound(true);
      }
    }, [showTooltip, storybook]);

    useEffect(() => {
      if (!isVerticallyBound && !isHorizontallyBound) {
        if (resolvedDirection === InfoTooltipDirection.BOTTOM_LEFT) {
          setResolvedDirection(InfoTooltipDirection.TOP_RIGHT);
        } else if (resolvedDirection === InfoTooltipDirection.BOTTOM_RIGHT) {
          setResolvedDirection(InfoTooltipDirection.TOP_LEFT);
        } else if (resolvedDirection === InfoTooltipDirection.TOP_LEFT) {
          setResolvedDirection(InfoTooltipDirection.BOTTOM_RIGHT);
        } else {
          setResolvedDirection(InfoTooltipDirection.BOTTOM_LEFT);
        }
      } else {
        if (!isVerticallyBound && isHorizontallyBound) {
          if (resolvedDirection === InfoTooltipDirection.BOTTOM_LEFT) {
            setResolvedDirection(InfoTooltipDirection.TOP_LEFT);
          } else if (resolvedDirection === InfoTooltipDirection.BOTTOM_RIGHT) {
            setResolvedDirection(InfoTooltipDirection.TOP_RIGHT);
          } else if (resolvedDirection === InfoTooltipDirection.TOP_LEFT) {
            setResolvedDirection(InfoTooltipDirection.BOTTOM_LEFT);
          } else {
            setResolvedDirection(InfoTooltipDirection.BOTTOM_RIGHT);
          }
        }
        if (isVerticallyBound && !isHorizontallyBound) {
          if (resolvedDirection === InfoTooltipDirection.BOTTOM_LEFT) {
            setResolvedDirection(InfoTooltipDirection.BOTTOM_RIGHT);
          } else if (resolvedDirection === InfoTooltipDirection.BOTTOM_RIGHT) {
            setResolvedDirection(InfoTooltipDirection.BOTTOM_LEFT);
          } else if (resolvedDirection === InfoTooltipDirection.TOP_LEFT) {
            setResolvedDirection(InfoTooltipDirection.TOP_RIGHT);
          } else {
            setResolvedDirection(InfoTooltipDirection.TOP_LEFT);
          }
        }
      }
      setIsHorizontallyBound(true);
      setIsVerticallyBound(true);
    }, [isHorizontallyBound, isVerticallyBound, resolvedDirection]);

    return (
      <div
        className="tooltipContainer"
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
      >
        <MdInfoOutline
          className={`infoIcon ${showTooltip ? "hover" : ""}`}
          style={dimensions}
          color={ICON_COLORS[iconColor]}
        />
        {rightLabel && <div className="rightLabel">{rightLabel}</div>}
        {showTooltip && (
          <span
            className={`tooltipText ${resolvedDirection}`}
            style={tooltipTextStyle}
            id="dropdownTooltipText"
          >
            {children}
          </span>
        )}
      </div>
    );
  }
);
